import { useState, useCallback, useEffect, useRef } from 'react';
import type { ListResponse, ListQuery } from '../types/api';

interface UseCrudPageOptions<T> {
  /**
   * Loader for a page of rows. MUST be referentially stable — either a module-level
   * function (`userApi.getAll`) or wrapped in `useCallback`.
   *
   * It is a deliberate load trigger: `load` lists it as a dependency so that a page
   * whose loader depends on a filter (see CompaniesPage's statusFilter) refetches
   * when that filter changes. The flip side is that an inline arrow function gets a
   * new identity on every render, which re-fires the effect, sets state, renders
   * again — an infinite fetch loop that flashes the grid and throws "Failed to
   * load data". Do not pass one.
   */
  fetchFn: (q: ListQuery) => Promise<ListResponse<T>>;
  pageSize?: number;
  initialSortCol?: string;
  initialSortDir?: 'Ascending' | 'Descending';
}

export function useCrudPage<T>({ fetchFn, pageSize = 25, initialSortCol = 'Id', initialSortDir = 'Ascending' }: UseCrudPageOptions<T>) {
  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortCol, setSortCol] = useState(initialSortCol);
  const [sortDir, setSortDir] = useState<'Ascending' | 'Descending'>(initialSortDir);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  // Only the most recent request may commit. Loads overlap routinely — a sort
  // click while a page change is in flight, or a fetchFn whose inputs settle
  // after mount — and responses can arrive out of order, letting a superseded
  // request overwrite fresh rows with its own (often empty) result.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;
    setLoading(true);
    try {
      const result = await fetchFn({ offset, limit: pageSize, sortCol, sortDirection: sortDir, searchText: searchText || undefined });
      if (!isCurrent()) return;
      setData(result.entities ?? []);
      setTotalCount(result.numberofEntities ?? 0);
    } catch {
      if (!isCurrent()) return;
      setToast({ message: 'Failed to load data', type: 'error' });
    } finally {
      // Leave `loading` set if a newer request is still running, so the grid
      // doesn't flash an empty state between overlapping loads.
      if (isCurrent()) setLoading(false);
    }
  }, [fetchFn, offset, pageSize, sortCol, sortDir, searchText]);

  useEffect(() => { load(); }, [load]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    // Auto-dismiss is owned by the Toast component (defaults to 15s, pauses on hover).
    setToast({ message, type });
  };

  const openCreate = () => { setEditingItem(null); setModalOpen(true); };
  const openEdit = (item: T) => { setEditingItem(item); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingItem(null); };

  return {
    data, totalCount, loading, offset, searchText, sortCol, sortDir,
    toast, modalOpen, editingItem, pageSize,
    setOffset, setSearchText, setSortCol, setSortDir, setToast,
    load, showToast, openCreate, openEdit, closeModal,
  };
}
